/**
 * EventDetailModal - Display detailed information about a specific event
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Descriptions,
  Tag,
  Alert,
  Spin,
  Divider,
  Typography,
  Card
} from 'antd';
import {
  WarningOutlined,
  CheckCircleOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import { getEventDetail } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Typography;

function EventDetailModal({ visible, onClose, eventId }) {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && eventId) {
      fetchEventDetail();
    }
  }, [visible, eventId]);

  const fetchEventDetail = async () => {
    setLoading(true);
    try {
      const response = await getEventDetail(eventId);
      if (response?.data) {
        setEvent(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch event details:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const renderMetadata = (metadata) => {
    if (!metadata) return 'N/A';
    if (typeof metadata === 'string') {
      try {
        metadata = JSON.parse(metadata);
      } catch (e) {
        return metadata;
      }
    }
    return (
      <pre style={{
        background: '#f5f5f5',
        padding: '12px',
        borderRadius: '4px',
        maxHeight: '300px',
        overflow: 'auto',
        fontSize: '12px'
      }}>
        {JSON.stringify(metadata, null, 2)}
      </pre>
    );
  };

  const getTierColor = (tier) => {
    const colors = {
      metadata_only: 'green',
      smart_selective: 'orange',
      full_events: 'red'
    };
    return colors[tier] || 'default';
  };

  const getTierDescription = (tier) => {
    const descriptions = {
      metadata_only: 'Only metadata stored (most privacy-preserving)',
      smart_selective: 'Selectively stored based on policies',
      full_events: 'Full event data stored'
    };
    return descriptions[tier] || 'Unknown tier';
  };

  return (
    <Modal
      title={`Event Details - ID: ${eventId}`}
      open={visible}
      onCancel={onClose}
      width={900}
      footer={null}
    >
      <Spin spinning={loading}>
        {event && (
          <>
            {/* PII Detection Alert */}
            {event.pii_detected && (
              <Alert
                message="PII Detected"
                description={
                  <div>
                    <Text>This event contains Personally Identifiable Information (PII).</Text>
                    <div style={{ marginTop: 8 }}>
                      <Text strong>PII Types: </Text>
                      {event.pii_types && event.pii_types.map((type, idx) => (
                        <Tag color="red" key={idx}>{type}</Tag>
                      ))}
                    </div>
                    {event.masking_enabled && (
                      <div style={{ marginTop: 8 }}>
                        <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                        <Text type="success">PII has been masked in the stored metadata</Text>
                      </div>
                    )}
                  </div>
                }
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Storage Tier Info */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <SafetyOutlined style={{ fontSize: 16 }} />
                <Text strong>Storage Tier: </Text>
                <Tag color={getTierColor(event.storage_tier)}>{event.storage_tier}</Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {getTierDescription(event.storage_tier)}
                </Text>
              </div>
            </Card>

            {/* Event Details */}
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Event ID" span={2}>
                {event.id}
              </Descriptions.Item>

              {/* TRACEABILITY: Project info */}
              <Descriptions.Item label="Project" span={2}>
                {event.project_name || event.project_id}
              </Descriptions.Item>

              {/* TRACEABILITY: Connector info */}
              {event.connector && (
                <Descriptions.Item label="Connector" span={2}>
                  <Text strong>{event.connector.name}</Text>
                  {' '}
                  <Tag color="cyan">{event.connector.type}</Tag>
                  {' '}
                  <Tag color={event.connector.status === 'active' ? 'green' : 'default'}>
                    {event.connector.status}
                  </Tag>
                </Descriptions.Item>
              )}
              {!event.connector && event.connector_id && (
                <Descriptions.Item label="Connector ID">
                  {event.connector_id}
                </Descriptions.Item>
              )}

              <Descriptions.Item label="Provider">
                <Tag color="blue">{event.provider}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Event Type">
                <Tag>{event.event_type}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Model" span={2}>
                {event.model || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Timestamp" span={2}>
                {new Date(event.ts).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Event Hash" span={2}>
                <Text code style={{ fontSize: 11 }}>{event.event_hash || 'N/A'}</Text>
              </Descriptions.Item>
              {event.payload_s3_key && (
                <Descriptions.Item label="S3 Payload Key" span={2}>
                  <Text code style={{ fontSize: 11 }}>{event.payload_s3_key}</Text>
                </Descriptions.Item>
              )}
              {event.archived_at && (
                <Descriptions.Item label="Archived At" span={2}>
                  <Tag color="orange">Archived</Tag> {new Date(event.archived_at).toLocaleString()}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Created At" span={2}>
                {new Date(event.created_at).toLocaleString()}
              </Descriptions.Item>
              {event.updated_at && (
                <Descriptions.Item label="Updated At" span={2}>
                  {new Date(event.updated_at).toLocaleString()}
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* UNIVERSAL TRACEABILITY: Control Violations */}
            {event.control_violations && event.control_violations.length > 0 && (
              <>
                <Divider>Control Violations ({event.control_violations.length})</Divider>
                <Alert
                  message="This event triggered the following control failures"
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                />
                {event.control_violations.map((violation, idx) => (
                  <Card
                    key={idx}
                    size="small"
                    style={{ marginBottom: 8 }}
                    title={
                      <div>
                        <Tag color={violation.status === 'fail' ? 'red' : 'orange'}>
                          {violation.control_code}
                        </Tag>
                        <Text strong>{violation.control_name}</Text>
                      </div>
                    }
                  >
                    <Descriptions size="small" column={2}>
                      {violation.control_category && (
                        <Descriptions.Item label="Category" span={2}>
                          <Tag>{violation.control_category}</Tag>
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label="Status">
                        <Tag color={violation.status === 'fail' ? 'red' : 'orange'}>
                          {violation.status}
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Confidence">
                        {violation.confidence}%
                      </Descriptions.Item>
                      <Descriptions.Item label="Evidence Count" span={2}>
                        {violation.evidence_count} events
                      </Descriptions.Item>
                      <Descriptions.Item label="Rationale" span={2}>
                        {violation.rationale}
                      </Descriptions.Item>
                      <Descriptions.Item label="Detected At" span={2}>
                        {new Date(violation.created_at).toLocaleString()}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                ))}
              </>
            )}

            {/* CTRL_01 PII Detection Decisions (Detailed Evidence) */}
            {event.ai01_decisions && event.ai01_decisions.length > 0 && (
              <>
                <Divider>PII Detection Evidence (CTRL_01)</Divider>
                {event.ai01_decisions.map((decision, idx) => (
                  <Card
                    key={idx}
                    size="small"
                    style={{ marginBottom: 8 }}
                    title={
                      <div>
                        <Text strong>Decision: </Text>
                        <Tag color={decision.decision === 'masked' ? 'orange' : decision.decision === 'blocked' ? 'red' : 'green'}>
                          {decision.decision}
                        </Tag>
                        {decision.severity && (
                          <Tag color={decision.severity === 'critical' ? 'red' : decision.severity === 'high' ? 'orange' : 'gold'}>
                            {decision.severity}
                          </Tag>
                        )}
                      </div>
                    }
                  >
                    <Descriptions size="small" column={2}>
                      {decision.reason && (
                        <Descriptions.Item label="Reason" span={2}>
                          {decision.reason}
                        </Descriptions.Item>
                      )}
                      {decision.detected_types && (
                        <Descriptions.Item label="PII Types Detected" span={2}>
                          {Object.entries(decision.detected_types).map(([type, count]) => (
                            <Tag key={type} color="red">{type}: {count}</Tag>
                          ))}
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label="Match Count">
                        {decision.match_count}
                      </Descriptions.Item>
                      <Descriptions.Item label="Confidence">
                        Max: {decision.confidence_max?.toFixed(1)}% / Avg: {decision.confidence_avg?.toFixed(1)}%
                      </Descriptions.Item>
                      {decision.action_taken && (
                        <Descriptions.Item label="Action" span={2}>
                          <Tag>{decision.action_taken}</Tag>
                        </Descriptions.Item>
                      )}
                      {decision.processing_mode && (
                        <Descriptions.Item label="Processing">
                          {decision.processing_mode}
                          {decision.was_truncated && <Tag color="orange" style={{ marginLeft: 4 }}>Truncated</Tag>}
                        </Descriptions.Item>
                      )}
                      {decision.original_size_kb && (
                        <Descriptions.Item label="Original Size">
                          {decision.original_size_kb.toFixed(2)} KB
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label="Decision Time" span={2}>
                        {new Date(decision.decision_at).toLocaleString()}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                ))}
              </>
            )}

            {/* Event Metadata */}
            <Divider>Event Metadata</Divider>
            {event.masking_enabled && (
              <Alert
                message="PII has been masked in the metadata below"
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
              />
            )}
            {renderMetadata(event.event_metadata)}

            {/* Storage Tier Notice */}
            {event.storage_tier === 'metadata_only' && (
              <Alert
                message="Privacy-First Storage"
                description="This event uses metadata-only storage. Full event payloads are not persisted, providing maximum privacy protection."
                type="info"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </>
        )}
      </Spin>
    </Modal>
  );
}

export default EventDetailModal;
